import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { ScopePicker } from '@/modules/backups/ScopePicker';
import { dialogSurface, flatPrimary } from '@/components/surface/tones';
import type { BackupDestination, BackupScope, BackupSource } from '@/types/api';

/**
 * Takes a backup now.
 *
 * ## The dialog closes as soon as the backup starts
 *
 * The request is synchronous and can run for minutes. Holding a modal open for that long would pin
 * the customer to a spinner with the page they came to look at hidden behind it, so the dialog
 * closes on submit and the backup appears at the top of the history as "Backing up…" until it
 * finishes. A refusal lands in the page's error message, which is not covered by anything by then.
 */
export function RunBackupDialog({
  open,
  onOpenChange,
  sources,
  destinations,
  sourceId,
  destinationId,
  scope,
  onSourceChange,
  onDestinationChange,
  onScopeChange,
  pickerHint,
  canSubmit,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: BackupSource[];
  destinations: BackupDestination[];
  sourceId: string;
  destinationId: string;
  scope: BackupScope;
  onSourceChange: (id: string) => void;
  onDestinationChange: (id: string) => void;
  onScopeChange: (scope: BackupScope) => void;
  pickerHint: string | null;
  canSubmit: boolean;
  onSubmit: () => void;
}) {
  const source = sources.find((item) => item.id === sourceId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-md ${dialogSurface}`}>
        <DialogHeader>
          <DialogTitle>Back up now</DialogTitle>
          <DialogDescription>A copy of one source, taken right away. This can take a few minutes.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="run-source">Source</Label>
            <Select id="run-source" value={sourceId} onChange={(event) => onSourceChange(event.target.value)}>
              <option value="">{sources.length === 0 ? 'No backup sources' : 'Choose a source…'}</option>
              {sources.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.server_name !== null ? ` (${item.server_name})` : ''}
                </option>
              ))}
            </Select>
            {pickerHint !== null && <p className="text-xs text-muted-foreground">{pickerHint}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="run-dest">To</Label>
            <Select id="run-dest" value={destinationId} onChange={(event) => onDestinationChange(event.target.value)}>
              <option value="">Choose a destination…</option>
              {destinations.map((destination) => (
                <option key={destination.id} value={destination.id}>
                  {destination.name}
                  {destination.is_offsite ? '' : ' (on the server)'}
                </option>
              ))}
            </Select>
            {destinations.length === 0 && (
              <p className="text-xs text-muted-foreground">No destinations yet. Add one before you can take a backup.</p>
            )}
          </div>

          <ScopePicker id="run-scope" value={scope} onChange={onScopeChange} source={source} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSubmit();
              onOpenChange(false);
            }}
            disabled={!canSubmit}
            className={flatPrimary}
          >
            <Play />
            Back up now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
